import { functions } from "../app/firebase.config";
import { httpsCallable } from "./firebaseCallable";
import { generateWithOllama } from "./ollamaClient";
import {
  LLM_PROVIDERS,
  LLM_SENSITIVITY,
  isCloudProvider,
  resolveLlmRoute,
} from "./llmRoutingPolicy";
import { parseJsonFromText, buildJsonRepairPrompt } from "./jsonUtils";

export const LLM_TELEMETRY_EVENT_NAME = "teachleague:llm-telemetry";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;
const SESSION_CACHE_PREFIX = "teachleague_llm_cache_v1::";
const SESSION_CACHE_INDEX_KEY = "teachleague_llm_cache_v1:index";
const DEFAULT_OPERATION_NAME = "generate";
const DEFAULT_MODEL_PREFERENCE = "default";
const GEMINI_MODEL_ALIASES = Object.freeze({
  "gemini-1.5-flash": "gemini-2.5-flash",
  "gemini-1.5-pro": "gemini-2.5-pro",
});

const normalizeGeminiModelName = (model) => {
  const normalized = String(model || "").trim().toLowerCase();
  if (!normalized) return "";
  return GEMINI_MODEL_ALIASES[normalized] || normalized;
};

const normalizeProviderModelName = (provider, model) => {
  const normalized = String(model || "").trim();
  if (!normalized) return "";
  if (provider === LLM_PROVIDERS.GEMINI) {
    return normalizeGeminiModelName(normalized);
  }
  return normalized;
};

const MODEL_PREFERENCES = Object.freeze({
  fast: Object.freeze({
    [LLM_PROVIDERS.OPENAI]:
      process.env.REACT_APP_OPENAI_FAST_MODEL || process.env.REACT_APP_OPENAI_MODEL || "gpt-4o-mini",
    [LLM_PROVIDERS.GEMINI]:
      normalizeGeminiModelName(
        process.env.REACT_APP_GEMINI_FAST_MODEL ||
          process.env.REACT_APP_GEMINI_MODEL ||
          "gemini-2.5-flash"
      ),
    [LLM_PROVIDERS.OLLAMA]:
      process.env.REACT_APP_OLLAMA_FAST_MODEL || process.env.REACT_APP_OLLAMA_MODEL || "llama3.1:8b",
  }),
  quality: Object.freeze({
    [LLM_PROVIDERS.OPENAI]:
      process.env.REACT_APP_OPENAI_QUALITY_MODEL || "gpt-4o",
    [LLM_PROVIDERS.GEMINI]:
      normalizeGeminiModelName(
        process.env.REACT_APP_GEMINI_QUALITY_MODEL || "gemini-2.5-pro"
      ),
    [LLM_PROVIDERS.OLLAMA]:
      process.env.REACT_APP_OLLAMA_MODEL || "llama3.1:8b",
  }),
});

const OPERATION_MODEL_PREFERENCE = Object.freeze({
  "repair-json": "fast",
  "distill-context": "fast",
});

const DEFAULT_JSON_REPAIR_SYSTEM_PROMPT =
  "You are a strict JSON repair tool. Return ONLY valid JSON that matches the requested schema.";

const CACHE_POLICY_BY_SENSITIVITY = Object.freeze({
  [LLM_SENSITIVITY.PUBLIC]: Object.freeze({
    enabled: true,
    ttlMs: 30 * 60 * 1000,
    maxEntries: 120,
    persistence: "session",
  }),
  [LLM_SENSITIVITY.INTERNAL]: Object.freeze({
    enabled: true,
    ttlMs: 20 * 60 * 1000,
    maxEntries: 100,
    persistence: "session",
  }),
  [LLM_SENSITIVITY.PRIVATE_VAULT]: Object.freeze({
    enabled: true,
    ttlMs: 5 * 60 * 1000,
    maxEntries: 40,
    persistence: "memory",
  }),
  [LLM_SENSITIVITY.STUDENT_PII]: Object.freeze({
    enabled: true,
    ttlMs: 5 * 60 * 1000,
    maxEntries: 40,
    persistence: "memory",
  }),
  [LLM_SENSITIVITY.CONFIDENTIAL]: Object.freeze({
    enabled: false,
    ttlMs: 0,
    maxEntries: 0,
    persistence: "none",
  }),
});

const DEFAULT_CACHE_POLICY = Object.freeze({
  enabled: false,
  ttlMs: 0,
  maxEntries: 0,
  persistence: "none",
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRateLimitError = (error) => {
  if (!error) return false;
  const errorString = String(error).toLowerCase();
  const errorMessage = String(error?.message || "").toLowerCase();
  return (
    errorString.includes("429") ||
    errorString.includes("rate limit") ||
    errorString.includes("quota") ||
    errorString.includes("resource-exhausted") ||
    errorMessage.includes("429") ||
    errorMessage.includes("rate limit") ||
    errorMessage.includes("quota")
  );
};

const normalizeObject = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeObject(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeObject(value[key]);
        return accumulator;
      }, {});
  }
  return value;
};

const stableStringify = (value) => JSON.stringify(normalizeObject(value));

const hashString = (value) => {
  const text = String(value || "");
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

const getSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const readSessionCacheIndex = (sessionStorageRef) => {
  if (!sessionStorageRef) return [];
  try {
    const raw = sessionStorageRef.getItem(SESSION_CACHE_INDEX_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((item) => String(item || "")) : [];
  } catch (error) {
    return [];
  }
};

const writeSessionCacheIndex = (sessionStorageRef, keys) => {
  if (!sessionStorageRef) return;
  try {
    sessionStorageRef.setItem(
      SESSION_CACHE_INDEX_KEY,
      JSON.stringify(Array.from(new Set(keys.filter(Boolean))))
    );
  } catch (error) {
    // Ignore storage write errors.
  }
};

class LlmOrchestrator {
  constructor() {
    this.generateCurriculum = httpsCallable(functions, "generateCurriculum");
    this.defaultProvider =
      process.env.REACT_APP_DEFAULT_AI_PROVIDER || LLM_PROVIDERS.GEMINI;
    this.fallbackEnabled = process.env.REACT_APP_ENABLE_AI_FALLBACK !== "false";
    this.cacheEnabled = process.env.REACT_APP_LLM_CACHE_ENABLED !== "false";
    this.memoryCache = new Map();
    this.telemetryHooks = new Set();
  }

  createTraceId() {
    return `llm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  getProviderName(provider) {
    if (provider === LLM_PROVIDERS.OPENAI) return "OpenAI";
    if (provider === LLM_PROVIDERS.GEMINI) return "Gemini";
    if (provider === LLM_PROVIDERS.OLLAMA) return "Ollama";
    return "Unknown";
  }

  getDefaultModelForProvider(provider) {
    if (provider === LLM_PROVIDERS.OPENAI) {
      return process.env.REACT_APP_OPENAI_MODEL || "gpt-4o-mini";
    }
    if (provider === LLM_PROVIDERS.GEMINI) {
      return normalizeGeminiModelName(
        process.env.REACT_APP_GEMINI_MODEL || "gemini-2.5-flash"
      );
    }
    return process.env.REACT_APP_OLLAMA_MODEL || "llama3.1:8b";
  }

  getPreferredModelForProvider(provider, preference = DEFAULT_MODEL_PREFERENCE) {
    if (preference === DEFAULT_MODEL_PREFERENCE) {
      return this.getDefaultModelForProvider(provider);
    }
    const mapped = MODEL_PREFERENCES?.[preference]?.[provider];
    return mapped || this.getDefaultModelForProvider(provider);
  }

  resolveModel({ provider, metadata = {}, options = {}, operation }) {
    const explicitModel =
      (typeof options?.model === "string" && options.model.trim()) ||
      (typeof metadata?.model === "string" && metadata.model.trim());
    if (explicitModel) {
      return normalizeProviderModelName(provider, explicitModel);
    }

    const preference =
      (typeof options?.modelPreference === "string" && options.modelPreference.trim()) ||
      (typeof metadata?.modelPreference === "string" &&
        metadata.modelPreference.trim()) ||
      OPERATION_MODEL_PREFERENCE[operation] ||
      DEFAULT_MODEL_PREFERENCE;

    return normalizeProviderModelName(
      provider,
      this.getPreferredModelForProvider(provider, preference)
    );
  }

  getFallbackProvider(currentProvider, allowedProviders = []) {
    const eligible = allowedProviders.filter(
      (provider) => provider !== currentProvider && isCloudProvider(provider)
    );
    return eligible[0] || null;
  }

  buildMetadata(route, metadata) {
    return {
      ...(metadata || {}),
      llmPolicyVersion: route.policyVersion,
      llmSensitivity: route.sensitivity,
      llmRequestedProvider: route.requestedProvider || null,
      llmResolvedProvider: route.provider,
      llmRequiresLocal: route.requiresLocal,
    };
  }

  buildCachePolicy(sensitivity) {
    if (!this.cacheEnabled) {
      return DEFAULT_CACHE_POLICY;
    }
    return CACHE_POLICY_BY_SENSITIVITY[sensitivity] || DEFAULT_CACHE_POLICY;
  }

  buildCacheKey({
    prompt,
    systemPrompt,
    route,
    provider,
    model,
    operation,
    tags,
  }) {
    const payload = stableStringify({
      prompt: String(prompt || ""),
      systemPrompt: String(systemPrompt || ""),
      provider,
      model,
      sensitivity: route?.sensitivity || "",
      policyVersion: route?.policyVersion || "",
      operation: String(operation || DEFAULT_OPERATION_NAME),
      llmContextTags: Array.isArray(tags) ? [...tags].sort() : [],
    });
    return hashString(payload);
  }

  emitTelemetry(eventType, payload = {}) {
    const eventPayload = {
      type: eventType,
      timestamp: new Date().toISOString(),
      ...payload,
    };

    this.telemetryHooks.forEach((hook) => {
      try {
        hook(eventPayload);
      } catch (error) {
        // Ignore hook errors to avoid blocking generation.
      }
    });

    if (typeof window !== "undefined") {
      try {
        window.dispatchEvent(
          new CustomEvent(LLM_TELEMETRY_EVENT_NAME, { detail: eventPayload })
        );
      } catch (error) {
        // Ignore browser dispatch errors.
      }
    }
  }

  addTelemetryHook(hook) {
    if (typeof hook !== "function") {
      return () => {};
    }
    this.telemetryHooks.add(hook);
    return () => {
      this.telemetryHooks.delete(hook);
    };
  }

  removeTelemetryHook(hook) {
    this.telemetryHooks.delete(hook);
  }

  loadSessionCacheEntry(cacheKey) {
    const sessionStorageRef = getSessionStorage();
    if (!sessionStorageRef) return null;
    try {
      const raw = sessionStorageRef.getItem(`${SESSION_CACHE_PREFIX}${cacheKey}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  saveSessionCacheEntry(cacheKey, entry) {
    const sessionStorageRef = getSessionStorage();
    if (!sessionStorageRef) return;
    try {
      sessionStorageRef.setItem(
        `${SESSION_CACHE_PREFIX}${cacheKey}`,
        JSON.stringify(entry)
      );
      const nextIndex = readSessionCacheIndex(sessionStorageRef).concat(cacheKey);
      writeSessionCacheIndex(sessionStorageRef, nextIndex);
    } catch (error) {
      // Ignore storage write errors.
    }
  }

  deleteSessionCacheEntry(cacheKey) {
    const sessionStorageRef = getSessionStorage();
    if (!sessionStorageRef) return;
    try {
      sessionStorageRef.removeItem(`${SESSION_CACHE_PREFIX}${cacheKey}`);
      const nextIndex = readSessionCacheIndex(sessionStorageRef).filter(
        (key) => key !== cacheKey
      );
      writeSessionCacheIndex(sessionStorageRef, nextIndex);
    } catch (error) {
      // Ignore storage delete errors.
    }
  }

  evictCacheEntry(cacheKey) {
    this.memoryCache.delete(cacheKey);
    this.deleteSessionCacheEntry(cacheKey);
  }

  getCacheEntry(cacheKey, cachePolicy) {
    const now = Date.now();
    const memoryEntry = this.memoryCache.get(cacheKey);
    if (memoryEntry) {
      if (memoryEntry.expiresAt <= now) {
        this.evictCacheEntry(cacheKey);
        return null;
      }
      return memoryEntry;
    }

    if (cachePolicy.persistence !== "session") {
      return null;
    }

    const sessionEntry = this.loadSessionCacheEntry(cacheKey);
    if (!sessionEntry) return null;
    if (Number(sessionEntry.expiresAt || 0) <= now) {
      this.deleteSessionCacheEntry(cacheKey);
      return null;
    }

    this.memoryCache.set(cacheKey, sessionEntry);
    return sessionEntry;
  }

  trimCacheForPolicy(sensitivity, cachePolicy) {
    if (!cachePolicy.enabled || cachePolicy.maxEntries <= 0) return;

    const matchingEntries = Array.from(this.memoryCache.entries())
      .filter(([, entry]) => entry?.sensitivity === sensitivity)
      .sort((left, right) => {
        const leftValue = Number(left[1]?.createdAt || 0);
        const rightValue = Number(right[1]?.createdAt || 0);
        return rightValue - leftValue;
      });

    if (matchingEntries.length <= cachePolicy.maxEntries) return;

    const keysToEvict = matchingEntries
      .slice(cachePolicy.maxEntries)
      .map(([key]) => key);

    keysToEvict.forEach((cacheKey) => {
      this.evictCacheEntry(cacheKey);
    });
  }

  storeCacheEntry(cacheKey, cachePolicy, response, sensitivity) {
    if (!cachePolicy.enabled || !cacheKey) return;

    const now = Date.now();
    const entry = {
      value: response,
      sensitivity,
      createdAt: now,
      expiresAt: now + cachePolicy.ttlMs,
      persistence: cachePolicy.persistence,
    };

    this.memoryCache.set(cacheKey, entry);

    if (cachePolicy.persistence === "session") {
      this.saveSessionCacheEntry(cacheKey, entry);
    }

    this.trimCacheForPolicy(sensitivity, cachePolicy);
  }

  clearCache({ sensitivity = null } = {}) {
    if (!sensitivity) {
      const keys = new Set(Array.from(this.memoryCache.keys()));
      const sessionStorageRef = getSessionStorage();
      readSessionCacheIndex(sessionStorageRef).forEach((cacheKey) =>
        keys.add(cacheKey)
      );
      Array.from(keys).forEach((cacheKey) => this.evictCacheEntry(cacheKey));
      return;
    }

    const keysToInspect = new Set(Array.from(this.memoryCache.keys()));
    const sessionStorageRef = getSessionStorage();
    readSessionCacheIndex(sessionStorageRef).forEach((cacheKey) =>
      keysToInspect.add(cacheKey)
    );

    Array.from(keysToInspect).forEach((cacheKey) => {
      const entry = this.memoryCache.get(cacheKey) || this.loadSessionCacheEntry(cacheKey);
      if (entry?.sensitivity === sensitivity) {
        this.evictCacheEntry(cacheKey);
      }
    });
  }

  getCacheStats() {
    const counts = {};
    this.memoryCache.forEach((entry) => {
      const sensitivity = entry?.sensitivity || "unknown";
      counts[sensitivity] = (counts[sensitivity] || 0) + 1;
    });
    return {
      inMemoryEntries: this.memoryCache.size,
      countsBySensitivity: counts,
    };
  }

  isProviderAvailable(provider) {
    return [
      LLM_PROVIDERS.OLLAMA,
      LLM_PROVIDERS.GEMINI,
      LLM_PROVIDERS.OPENAI,
    ].includes(provider);
  }

  getAvailableProviders() {
    return [LLM_PROVIDERS.OLLAMA, LLM_PROVIDERS.GEMINI, LLM_PROVIDERS.OPENAI];
  }

  async generateWithCloudProvider({
    prompt,
    provider,
    model,
    metadata,
    systemPrompt,
  }) {
    return this.generateCurriculum({
      prompt,
      provider,
      model,
      metadata,
      ...(systemPrompt ? { systemPrompt } : {}),
    });
  }

  async generate(prompt, preferredProvider = null, metadata = null, options = {}) {
    const operation =
      typeof metadata?.operation === "string" && metadata.operation.trim()
        ? metadata.operation.trim()
        : DEFAULT_OPERATION_NAME;
    const requestedMetadata =
      metadata && typeof metadata === "object" ? metadata : {};
    const systemPrompt =
      typeof requestedMetadata.systemPrompt === "string"
        ? requestedMetadata.systemPrompt
        : "";

    const route = resolveLlmRoute({
      requestedProvider: preferredProvider || this.defaultProvider,
      metadata: requestedMetadata,
    });

    const provider = route.provider;
    const traceId =
      typeof options?.traceId === "string" && options.traceId.trim()
        ? options.traceId.trim()
        : this.createTraceId();
    const promptHash = hashString(`${systemPrompt}\n\n${String(prompt || "")}`);
    const model = this.resolveModel({
      provider,
      metadata: requestedMetadata,
      options,
      operation,
    });
    const requestMetadata = this.buildMetadata(route, requestedMetadata);
    const llmContextTags = Array.isArray(requestMetadata?.llmContextTags)
      ? requestMetadata.llmContextTags
      : [];
    const cachePolicy = this.buildCachePolicy(route.sensitivity);
    const useCache = options?.useCache !== false;
    const cacheKey =
      useCache && cachePolicy.enabled
        ? this.buildCacheKey({
            prompt,
            systemPrompt,
            route,
            provider,
            model,
            operation,
            tags: llmContextTags,
          })
        : null;

    this.emitTelemetry("generate_start", {
      traceId,
      operation,
      promptHash,
      promptLength: String(prompt || "").length,
      provider,
      model,
      sensitivity: route.sensitivity,
      requiresLocal: route.requiresLocal,
      policyVersion: route.policyVersion,
    });

    if (cacheKey && useCache && cachePolicy.enabled) {
      const cachedEntry = this.getCacheEntry(cacheKey, cachePolicy);
      if (cachedEntry?.value) {
        this.emitTelemetry("cache_hit", {
          traceId,
          operation,
          cacheKey,
          sensitivity: route.sensitivity,
        });
        return {
          ...cachedEntry.value,
          cache: {
            hit: true,
            key: cacheKey,
            policy: cachePolicy,
          },
        };
      }
      this.emitTelemetry("cache_miss", {
        traceId,
        operation,
        cacheKey,
        sensitivity: route.sensitivity,
      });
    }

    const respondWithPolicy = (result) => ({
      ...result,
      llmPolicy: {
        sensitivity: route.sensitivity,
        requiresLocal: route.requiresLocal,
        policyVersion: route.policyVersion,
      },
    });

    if (provider === LLM_PROVIDERS.OLLAMA || route.requiresLocal) {
      this.emitTelemetry("attempt_start", {
        traceId,
        operation,
        provider: LLM_PROVIDERS.OLLAMA,
        model: this.resolveModel({
          provider: LLM_PROVIDERS.OLLAMA,
          metadata: requestedMetadata,
          options,
          operation,
        }),
        attempt: 1,
      });

      try {
        const localResult = await generateWithOllama({
          prompt,
          systemPrompt,
          model: this.resolveModel({
            provider: LLM_PROVIDERS.OLLAMA,
            metadata: requestedMetadata,
            options,
            operation,
          }),
        });

        const response = respondWithPolicy({
          content: localResult.content,
          provider: LLM_PROVIDERS.OLLAMA,
          providerName: this.getProviderName(LLM_PROVIDERS.OLLAMA),
          model:
            localResult.model ||
            this.resolveModel({
              provider: LLM_PROVIDERS.OLLAMA,
              metadata: requestedMetadata,
              options,
              operation,
            }),
          usedFallback: false,
          tokensUsed: null,
          telemetryTraceId: traceId,
        });

        this.storeCacheEntry(cacheKey, cachePolicy, response, route.sensitivity);
        this.emitTelemetry("attempt_success", {
          traceId,
          operation,
          provider: LLM_PROVIDERS.OLLAMA,
          model: response.model,
          attempt: 1,
        });
        this.emitTelemetry("generate_success", {
          traceId,
          operation,
          provider: response.provider,
          model: response.model,
          usedFallback: false,
          cacheStored: Boolean(cacheKey && cachePolicy.enabled),
        });
        return response;
      } catch (error) {
        this.emitTelemetry("attempt_error", {
          traceId,
          operation,
          provider: LLM_PROVIDERS.OLLAMA,
          attempt: 1,
          message: String(error?.message || error || "Unknown error"),
        });
        this.emitTelemetry("generate_error", {
          traceId,
          operation,
          provider: LLM_PROVIDERS.OLLAMA,
          message: String(error?.message || error || "Unknown error"),
        });
        throw error;
      }
    }

    let lastError = null;
    let retryDelayMs = INITIAL_RETRY_DELAY_MS;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
      this.emitTelemetry("attempt_start", {
        traceId,
        operation,
        provider,
        model,
        attempt,
      });
      try {
        const result = await this.generateWithCloudProvider({
          prompt,
          provider,
          model,
          metadata: requestMetadata,
          systemPrompt,
        });

        const response = respondWithPolicy({
          content: result.data.content,
          provider: result.data.provider || provider,
          providerName: this.getProviderName(result.data.provider || provider),
          model: result.data.model || model,
          usedFallback: Boolean(result.data.usedFallback),
          fallbackReason: result.data.fallbackReason || null,
          tokensUsed: result.data.tokensUsed || null,
          telemetryTraceId: traceId,
        });

        this.storeCacheEntry(cacheKey, cachePolicy, response, route.sensitivity);
        this.emitTelemetry("attempt_success", {
          traceId,
          operation,
          provider: response.provider,
          model: response.model,
          attempt,
        });
        this.emitTelemetry("generate_success", {
          traceId,
          operation,
          provider: response.provider,
          model: response.model,
          usedFallback: response.usedFallback,
          cacheStored: Boolean(cacheKey && cachePolicy.enabled),
        });
        return response;
      } catch (error) {
        lastError = error;
        this.emitTelemetry("attempt_error", {
          traceId,
          operation,
          provider,
          model,
          attempt,
          message: String(error?.message || error || "Unknown error"),
        });

        if (isRateLimitError(error) && attempt < MAX_RETRIES) {
          await sleep(retryDelayMs);
          retryDelayMs *= 2;
        } else if (attempt < MAX_RETRIES) {
          await sleep(500);
        }
      }
    }

    if (this.fallbackEnabled && route.allowFallback) {
      const fallbackProvider = this.getFallbackProvider(provider, route.allowedProviders);
      if (fallbackProvider) {
        const fallbackModel = this.resolveModel({
          provider: fallbackProvider,
          metadata: requestedMetadata,
          options,
          operation,
        });
        this.emitTelemetry("fallback_start", {
          traceId,
          operation,
          fromProvider: provider,
          toProvider: fallbackProvider,
        });

        try {
          const result = await this.generateWithCloudProvider({
            prompt,
            provider: fallbackProvider,
            model: fallbackModel,
            metadata: requestMetadata,
            systemPrompt,
          });

          const response = respondWithPolicy({
            content: result.data.content,
            provider: result.data.provider || fallbackProvider,
            providerName: this.getProviderName(
              result.data.provider || fallbackProvider
            ),
            model: result.data.model || fallbackModel,
            usedFallback: true,
            fallbackReason:
              result.data.fallbackReason ||
              (isRateLimitError(lastError) ? "rate_limit" : "error"),
            tokensUsed: result.data.tokensUsed || null,
            telemetryTraceId: traceId,
          });

          this.storeCacheEntry(cacheKey, cachePolicy, response, route.sensitivity);
          this.emitTelemetry("fallback_success", {
            traceId,
            operation,
            provider: response.provider,
            model: response.model,
          });
          this.emitTelemetry("generate_success", {
            traceId,
            operation,
            provider: response.provider,
            model: response.model,
            usedFallback: true,
            cacheStored: Boolean(cacheKey && cachePolicy.enabled),
          });
          return response;
        } catch (fallbackError) {
          this.emitTelemetry("fallback_error", {
            traceId,
            operation,
            fromProvider: provider,
            toProvider: fallbackProvider,
            message: String(
              fallbackError?.message || fallbackError || "Unknown error"
            ),
          });
          lastError = fallbackError;
        }
      }
    }

    const finalError = new Error(
      `Failed to generate content with ${provider} after ${MAX_RETRIES} attempts. ` +
        `Error: ${lastError?.message || "Unknown error"}`
    );
    this.emitTelemetry("generate_error", {
      traceId,
      operation,
      provider,
      model,
      message: finalError.message,
    });
    throw finalError;
  }

  async generateStructuredJson({
    prompt,
    preferredProvider = null,
    metadata = null,
    options = {},
    schemaHint = "",
    maxRepairAttempts = 1,
    repairSystemPrompt = DEFAULT_JSON_REPAIR_SYSTEM_PROMPT,
  } = {}) {
    const initialResult = await this.generate(
      prompt,
      preferredProvider,
      metadata,
      options
    );
    const initialParsed = parseJsonFromText(initialResult?.content);
    if (initialParsed || maxRepairAttempts <= 0) {
      return {
        ...initialResult,
        parsed: initialParsed,
        raw: initialResult?.content || "",
        usedRepair: false,
      };
    }

    const repairPrompt = buildJsonRepairPrompt({
      rawText: initialResult?.content,
      schemaHint,
    });
    const repairMetadata = {
      ...(metadata || {}),
      operation: "repair-json",
      systemPrompt: repairSystemPrompt,
    };
    const repairOptions = {
      ...options,
      modelPreference: "fast",
      useCache: false,
    };
    const repairResult = await this.generate(
      repairPrompt,
      preferredProvider,
      repairMetadata,
      repairOptions
    );
    const repairedParsed = parseJsonFromText(repairResult?.content);

    return {
      ...repairResult,
      parsed: repairedParsed,
      raw: repairResult?.content || "",
      original: initialResult?.content || "",
      usedRepair: true,
    };
  }
}

let orchestratorInstance = null;

export const getLlmOrchestrator = () => {
  if (!orchestratorInstance) {
    orchestratorInstance = new LlmOrchestrator();
  }
  return orchestratorInstance;
};

export default getLlmOrchestrator;
